import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, updateDoc, setDoc, addDoc, arrayUnion, query, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { X, Send, AlertCircle, Loader2 } from 'lucide-react';

const MigratePlayersModal = ({ selectedPlayers, sourceClub, currentUser, onClose, onSuccess }) => {
  const [clubs, setClubs] = useState([]);
  const [targetClubId, setTargetClubId] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingClubs, setFetchingClubs] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const fetchClubs = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'club'));
        const clubList = [];
        snapshot.forEach(docSnap => {
          if (docSnap.id !== sourceClub.id) {
            clubList.push({ id: docSnap.id, ...docSnap.data() });
          }
        });
        setClubs(clubList.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        console.error('Error fetching clubs:', err);
      } finally {
        setFetchingClubs(false);
      }
    };
    fetchClubs();
  }, [sourceClub.id]);

  const handleMigrate = async () => {
    if (!targetClubId || selectedPlayers.length === 0) return;
    
    const targetClub = clubs.find(c => c.id === targetClubId);
    if (!targetClub) return;

    if (!confirm(`Are you sure you want to migrate ${selectedPlayers.length} players to ${targetClub.name}?\n\nThey will be auto-approved in the new club and assigned their initial $250 / $125 donation accordingly.`)) {
      return;
    }

    setLoading(true);
    setProgress(0);

    try {
      for (let i = 0; i < selectedPlayers.length; i++) {
        const player = selectedPlayers[i];
        
        let feeAmount = 125; // Default fallback
        let isLeather = false;
        let isTennis = false;
        
        try {
          console.log(`Getting reg form for ${player.id}`);
          const regFormSnap = await getDoc(doc(db, 'registrationForms', player.id));
          if (regFormSnap.exists()) {
            const data = regFormSnap.data();
            if (data.formats?.leather) {
              feeAmount = 250;
              isLeather = true;
            } else if (data.formats?.tennis) {
              feeAmount = 125;
              isTennis = true;
            }
          }
        } catch (err) {
          console.warn("Could not fetch registration form for", player.id, err);
        }
        
        try {
          console.log(`Updating user ${player.id}`);
          const userRef = doc(db, 'users', player.id);
          const updatePayload = {
            [`joinedClubs.${targetClubId}`]: {
              clubId: targetClubId,
              clubName: targetClub.name,
              role: 'player',
              status: 'approved',
              joinedAt: new Date().toISOString()
            },
            joinedClubsIds: arrayUnion(targetClubId)
          };
          await updateDoc(userRef, updatePayload);
          
          console.log(`Adding expense for ${player.id}`);
          const q = query(
            collection(db, 'expenses'),
            where('clubId', '==', targetClubId),
            where('isInitialDonation', '==', true),
            where('splitAmong', 'array-contains', player.id)
          );
          const existingExpenses = await getDocs(q);
          
          if (existingExpenses.empty) {
            const expenseData = {
              description: 'Initial Donation',
              amount: feeAmount,
              amountPerPlayer: feeAmount,
              gameId: null,
              gameName: null,
              tournamentId: null,
              tournamentName: null,
              clubId: targetClubId,
              splitAmong: [player.id],
              receiptURL: null,
              createdAt: new Date().toISOString(),
              submittedBy: currentUser?.uid || 'system',
              submitterName: currentUser?.displayName || 'System Admin',
              approvalStatus: 'approved',
              approvedBy: 'system',
              approvedAt: new Date().toISOString(),
              isInitialDonation: true,
              isRegistrationFee: false, // Match logic
              playerEmail: player.email,
              playerName: player.name,
              isDividable: false,
              expenseType: 'Initial Donation',
              notes: `Auto-generated from club migration to ${targetClub.name}. ${isLeather ? '(Leather - $250)' : isTennis ? '(Tennis - $125)' : '(Default - $125)'}`
            };
            await addDoc(collection(db, 'expenses'), expenseData);
            
            console.log(`Getting/updating balance for ${player.id}`);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const currentBalance = userSnap.data().balance || 0;
              await updateDoc(userRef, { balance: currentBalance + feeAmount });
            }
            
            console.log(`Adding audit log for ${player.id}`);
            await addDoc(collection(db, 'auditLogs'), {
              action: 'PLAYER_MIGRATED',
              targetUserId: player.id,
              targetUserName: player.name,
              performedBy: currentUser?.uid || 'system',
              performedByName: currentUser?.displayName || 'System Admin',
              timestamp: new Date().toISOString(),
              sourceClubId: sourceClub.id,
              targetClubId: targetClub.id,
              feeApplied: feeAmount
            });
          } else {
            console.log(`Expense already exists for ${player.id}, skipping.`);
          }
        } catch (innerErr) {
          console.error(`Step failed for ${player.id}:`, innerErr);
          throw new Error(`Failed at step for ${player.name}: ${innerErr.message}`);
        }

        setProgress(Math.round(((i + 1) / selectedPlayers.length) * 100));
      }
      
      onSuccess();
    } catch (err) {
      console.error('Migration failed:', err);
      alert(`Migration Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b bg-gray-50">
          <h2 className="text-xl font-bold">Migrate Players</h2>
          <button onClick={onClose} disabled={loading} className="p-1 text-gray-500 hover:bg-gray-200 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6">
          <div className="mb-6 p-4 bg-blue-50 text-blue-800 rounded-lg flex gap-3 border border-blue-100">
            <AlertCircle className="flex-shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed">
              You are migrating <strong>{selectedPlayers.length}</strong> players. They will be immediately approved in the new club, and safely assigned a <strong>$250 (Leather)</strong> or <strong>$125 (Tennis)</strong> Initial Donation based on their original format.
            </p>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-semibold mb-2 text-gray-700">Select Destination Club</label>
            {fetchingClubs ? (
              <div className="p-3 bg-gray-50 rounded text-sm flex items-center text-gray-600 gap-2 border">
                <Loader2 className="animate-spin text-blue-500" size={16} /> Loading clubs from network...
              </div>
            ) : (
              <select 
                value={targetClubId}
                onChange={(e) => setTargetClubId(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                disabled={loading}
              >
                <option value="">-- Choose a destination club --</option>
                {clubs.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            )}
          </div>
          
          {loading && (
            <div className="mb-6 mt-6">
              <div className="flex justify-between text-xs font-semibold text-gray-600 mb-2">
                <span>Migrating Players...</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden border border-gray-200">
                <div className="bg-blue-600 h-2.5 transition-all duration-300 ease-out" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-3 mt-8 pt-4 border-t">
            <button 
              onClick={onClose}
              disabled={loading}
              className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-100 rounded-lg transition-colors border border-transparent hover:border-gray-200"
            >
              Cancel
            </button>
            <button 
              onClick={handleMigrate}
              disabled={loading || !targetClubId}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 font-medium disabled:opacity-50 disabled:hover:bg-blue-600 transition-all shadow-sm"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
              {loading ? 'Processing Migration...' : 'Confirm Migration'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MigratePlayersModal;
